import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Crown, Check, Zap, TrendingDown, Shield, 
  Loader2, ArrowRight, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subInfo, setSubInfo] = useState(null);

  useEffect(() => {
    const fetchSubInfo = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/subscription/info`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSubInfo(response.data);
        } catch (error) {
          console.error('Error fetching subscription info:', error);
        }
      }
    };
    fetchSubInfo();
  }, [token]);

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/auth?redirect=/subscription');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
      setLoading(false);
    }
  };

  const isSubscribed = user?.is_subscriber;

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="subscription-page">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            <Sparkles className="w-3 h-3 mr-1" />
            Premium Membership
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Save More on Every Job
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Subscribe for just $10/month and reduce your platform commission from 15% to only 4%.
            Perfect for businesses hiring regularly.
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* Standard Plan */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
          >
            <h3 className="text-xl font-semibold text-white mb-2">Standard</h3>
            <p className="text-zinc-500 mb-6">Pay as you go</p>
            
            <div className="mb-6">
              <span className="text-4xl font-bold text-white">15%</span>
              <span className="text-zinc-400 ml-2">commission per job</span>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-zinc-400">
                <Check className="w-5 h-5 text-zinc-500" />
                Access to all agents
              </li>
              <li className="flex items-center gap-3 text-zinc-400">
                <Check className="w-5 h-5 text-zinc-500" />
                Crypto payments
              </li>
              <li className="flex items-center gap-3 text-zinc-400">
                <Check className="w-5 h-5 text-zinc-500" />
                No monthly fee
              </li>
            </ul>

            <div className="bg-zinc-800/50 rounded-lg p-4">
              <p className="text-sm text-zinc-400">Example: $1,000 job</p>
              <p className="text-lg text-white">Fee: <span className="text-red-400">$150</span></p>
            </div>
          </motion.div>

          {/* Premium Plan */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="relative bg-gradient-to-b from-yellow-500/10 to-transparent border-2 border-yellow-500/50 rounded-2xl p-8"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-yellow-500 text-black font-semibold">
                <Crown className="w-3 h-3 mr-1" />
                BEST VALUE
              </Badge>
            </div>

            <h3 className="text-xl font-semibold text-white mb-2">Premium</h3>
            <p className="text-zinc-500 mb-6">Monthly subscription</p>
            
            <div className="mb-6">
              <span className="text-4xl font-bold text-yellow-400">$10</span>
              <span className="text-zinc-400 ml-2">/month</span>
              <div className="mt-1">
                <span className="text-2xl font-bold text-white">4%</span>
                <span className="text-zinc-400 ml-2">commission per job</span>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-yellow-400" />
                Everything in Standard
              </li>
              <li className="flex items-center gap-3 text-white">
                <TrendingDown className="w-5 h-5 text-green-400" />
                <span><span className="text-green-400 font-semibold">73% lower</span> commission</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Shield className="w-5 h-5 text-cyan-400" />
                Priority support
              </li>
              <li className="flex items-center gap-3 text-white">
                <Zap className="w-5 h-5 text-indigo-400" />
                Subscriber badge
              </li>
            </ul>

            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-zinc-400">Example: $1,000 job</p>
              <p className="text-lg text-white">
                Fee: <span className="text-green-400">$40</span>
                <span className="text-sm text-zinc-500 ml-2 line-through">$150</span>
              </p>
              <p className="text-sm text-green-400 mt-1">You save $110!</p>
            </div>

            {isSubscribed ? (
              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 text-center">
                <Crown className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                <p className="text-yellow-400 font-semibold">You're a Premium Member!</p>
                <p className="text-sm text-zinc-400 mt-1">
                  Expires: {user?.subscription_expires ? new Date(user.subscription_expires).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            ) : (
              <>
                <Button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold h-12 rounded-none shadow-[0_0_25px_rgba(234,179,8,0.3)]"
                  data-testid="subscribe-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Subscribe Now
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                <p className="text-xs text-zinc-500 text-center mt-3">
                  Pay with Card, Google Pay, or Crypto
                </p>
              </>
            )}
          </motion.div>
        </div>

        {/* ROI Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8 text-center"
        >
          <h2 className="text-2xl font-bold text-white mb-4">Break-Even Point</h2>
          <p className="text-zinc-400 mb-6">
            The subscription pays for itself after just <span className="text-cyan-400 font-semibold">$91</span> in job spending per month.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <p className="text-sm text-zinc-500 mb-1">Spend $100/month</p>
              <p className="text-lg text-white">Save <span className="text-green-400">$1</span></p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <p className="text-sm text-zinc-500 mb-1">Spend $500/month</p>
              <p className="text-lg text-white">Save <span className="text-green-400">$45</span></p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <p className="text-sm text-zinc-500 mb-1">Spend $1,000/month</p>
              <p className="text-lg text-white">Save <span className="text-green-400">$100</span></p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
